/* eslint-disable no-unused-vars */
//For quotation data fetching management
import { unwrapResult } from '@reduxjs/toolkit';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { useSnackBar } from '../use-snackbar';
import {
  CheckQuotationAvailabilityRequest,
  ComputeSRPRequest,
  GetQuotationProductsRequest,
  GetTemplateRequest,
  NewTemplateDetails,
  Quotation
} from 'src/types';
import _ from 'lodash';
import { validateVoucherThunk } from 'src/redux/slices/voucher/thunks';

const {
  selectors: quotationSelectors,
  actions: quotationActions
} = slices.quotationV2;

const useQuotationApi = () => {
  const dispatch = useAppDispatch();
  const snackBar = useSnackBar();

  const isLoadingQuotation = useAppSelector(
    quotationSelectors?.isLoadingQuotation
  );
  const { is_category_loading, categoryOptions } = useAppSelector(
    quotationSelectors?.selectCategory
  );

  const { is_template_loading, templateOptions } = useAppSelector(
    quotationSelectors?.selectTemplate
  );

  const totalAmount = useAppSelector(quotationSelectors?.selectTotalAmount);

  const fetchCategoryOption = async () => {
    try {
      const response = unwrapResult(
        await dispatch(quotationActions?.getQuotationsCategoriesThunk())
      );

      if (!response?.success) {
        snackBar?.show({
          severity: 'error',
          message: response?.message || 'Failed to Load quotation category'
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchQuotationProducts = async (
    params: GetQuotationProductsRequest,
    reset: boolean = false
  ) => {
    try {
      if (reset) {
        dispatch(quotationActions.resetProductOptionsAction());
      }
      const response = unwrapResult(
        await dispatch(quotationActions.getQuotationsProductsThunk(params))
      );

      if (!response?.success) {
        snackBar?.show({
          severity: 'error',
          message: response?.message || 'Failed to load quotation products'
        });
        return;
      }

      const { meta } = response.originalData;
      const totalPages = meta?.last_page ?? 1;

      for (let page = 2; page <= totalPages; page++) {
        console.info('Fetching page:', page);

        const nextResponse = unwrapResult(
          await dispatch(
            quotationActions.getQuotationsProductsThunk({
              ...params,
              page
            })
          )
        );

        if (!nextResponse?.success) {
          console.error(`Failed to fetch page ${page}`);
          break;
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const checkQuotationAvailability = async (
    params: CheckQuotationAvailabilityRequest
  ) => {
    const cleanItems = params?.items?.filter(
      (item) =>
        item?.product?.product_name !== undefined &&
        item?.product?.product_name !== ''
    );

    try {
      const response = unwrapResult(
        await dispatch(
          quotationActions?.checkQuotationAvailabilityThunk({
            ...params,
            items: cleanItems
          })
        )
      );

      if (!response?.success) {
        snackBar?.show({
          severity: 'error',
          message: response?.message || 'Failed to check quotation availability'
        });
      }

      return response;
    } catch (error) {
      console.error(error);
    }
  };

  //TODO: this is a action function, this is not a pure function
  const checkQuotationItemsAvailability = async (
    params: CheckQuotationAvailabilityRequest
  ) => {
    try {
      const cleanItems = params?.items
        ?.filter(
          (item) =>
            item?.product?.product_name !== undefined &&
            item?.product?.product_name !== ''
        )
        .map((item) => ({ ...item, warning_note: undefined }));

      const response = unwrapResult(
        await dispatch(
          quotationActions?.checkQuotationAvailabilityThunk({
            items: cleanItems,
            branch_id: params?.branch_id
          })
        )
      );

      const hasWarningNotes = response?.originalData?.quote_items.some(
        (items) => items?.warning_note
      );

      if (hasWarningNotes) {
        snackBar?.show({
          severity: 'error',
          message:
            'There are some item that is currently invalid or out of stock, kindly double check to confirm this action.'
        });
      } else {
        snackBar?.show({
          severity: 'success',
          useSound: true,
          message: 'All Products is available.'
        });
      }

      if (!response?.success || response?.errors) {
        snackBar?.show({
          severity: 'error',
          message: response?.message || 'Failed to check quotation availability'
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  //TEMPLATES
  const fetchTemplate = async (params: GetTemplateRequest) => {
    try {
      const response = unwrapResult(
        await dispatch(quotationActions?.getTemplateListThunk(params))
      );

      if (!response?.success) {
        snackBar?.show({
          severity: 'error',
          message: response?.message || 'Failed to Load quotation template'
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchTemplateDetails = async (params: GetTemplateRequest) => {
    try {
      const response = unwrapResult(
        await dispatch(quotationActions?.getTemplateDetailsThunk(params))
      );

      if (!response?.success) {
        snackBar?.show({
          severity: 'error',
          message: response?.message || 'Failed to Load template detail'
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const createTemplate = async (params: NewTemplateDetails) => {
    try {
      const response = unwrapResult(
        await dispatch(quotationActions?.createTemplateThunk(params))
      );

      if (!response?.success) {
        snackBar?.show({
          severity: 'error',
          message: response?.message || 'Failed to create template '
        });
      }

      if (response?.success) {
        fetchTemplateDetails({
          template_id: response?.originalData?.data?.quotation_id
        });
      }
      return response;
    } catch (error) {
      console.error(error);
    }
  };

  const updateTemplate = async (params: NewTemplateDetails) => {
    try {
      const response = unwrapResult(
        await dispatch(quotationActions?.updateTemplateThunk(params))
      );

      if (!response?.success) {
        snackBar?.show({
          severity: 'error',
          message: response?.message || 'Failed to update template '
        });
      }

      if (response?.success) {
        fetchTemplateDetails({
          template_id: response?.originalData?.data?.quotation_id
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const deleteTemplate = async (params: number) => {
    try {
      const response = unwrapResult(
        await dispatch(quotationActions?.onDeleteTemplateThunk(params))
      );

      if (!response?.success) {
        snackBar?.show({
          severity: 'error',
          message: response?.message || 'Failed to delete template '
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const computeSrp = async (srp_value: number, params: Quotation[]) => {
    try {
      const cleanItems = params?.filter(
        (item) =>
          item?.product?.product_name !== undefined &&
          item?.product?.product_name !== ''
      );

      const productAndQuantity = cleanItems?.map((item) => ({
        product_id: item?.product_id,
        quantity: item?.quantity,
        product_name: item?.product?.product_name,
        retail_price: item?.product?.retail_price
      }));

      const srpApplyData: ComputeSRPRequest = {
        products: productAndQuantity,
        srp_value
      };
      const response = unwrapResult(
        await dispatch(quotationActions?.onComputeSrpThunk(srpApplyData))
      );

      if (response?.errors) {
        snackBar?.show({
          severity: 'error',
          message: 'Failed to compute srp '
        });
        return;
      }

      if (response?.success) {
        snackBar?.show({
          severity: 'success',
          message: 'Successfully applied srp price',
          useSound: true
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const validateVoucher = async (
    voucherCode: string,
    totalAmount: number,
    quotationData: Quotation[]
  ) => {
    try {
      const response = unwrapResult(
        await dispatch(
          validateVoucherThunk({
            voucher_code: voucherCode || '',
            total_amount: totalAmount,
            products: quotationData
              .filter(
                (item) =>
                  item.product?.product_id &&
                  item.product?.category_id !== 35 &&
                  item.product?.category_id !== 52
              )
              .map((item) => ({
                product_id: item.product?.product_id,
                product_name: item.product?.product_name,
                amount: item.product?.retail_price,
                category_id: item.product?.category_id,
                quantity: Number(item.quantity || 0)
              }))
          })
        )
      );

      if (!response?.success) {
        const { errors, message } = response;

        snackBar.show({
          severity: 'error',
          message: errors
            ? Object.values(errors).join(', ')
            : message || 'Voucher is not valid'
        });
        return;
      }

      return response;
    } catch (error) {
      console.error('Error apply voucher:', error);
      snackBar.show({
        severity: 'error',
        message: 'An error occurred while apply the voucher'
      });
    }
  };

  return {
    isLoadingQuotation,
    is_category_loading,
    is_template_loading,
    categoryOptions,
    templateOptions,
    totalAmount,

    fetchCategoryOption,
    fetchQuotationProducts,
    checkQuotationAvailability,
    checkQuotationItemsAvailability,

    fetchTemplate,
    fetchTemplateDetails,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    computeSrp,
    validateVoucher
  };
};

export default useQuotationApi;
